import { EditOutlined, PlusCircleOutlined } from "@ant-design/icons"
import { Button, Flex, Form, Input, message } from "antd"
import Title from "antd/es/typography/Title"
import React, { useState } from "react"
import { t } from "~/languages/i18n"
import { checkSlug, createCategory } from "../services/category"

interface FormAddCategoryProps {
  onCategoryAdded: () => void
}

const FormAddCategory: React.FC<FormAddCategoryProps> = ({ onCategoryAdded }) => {
  const [form] = Form.useForm()
  const [isEditableSlug, setIsEditableSlug] = useState(true)
  const permissions = JSON.parse(localStorage.getItem("permissions") || "{}")

  const removeAccents = (str: string) => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[đĐ]/g, "d")
      .replace(/[âÂ]/g, "a")
      .replace(/[êÊ]/g, "e")
      .replace(/[.,:"'<>?`!@#$%^&*();/\\]/g, "")
      .replace(/\s+/g, "-")
      .toLowerCase()
  }
  const handleNameBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const nameValue = e.target.value.trim()
    if (!nameValue) {
      form.setFields([{ name: "slug", errors: [] }])
      return
    }

    const slugValue = removeAccents(nameValue)

    const isSlugUnique = async (slug: string): Promise<boolean> => {
      try {
        const response: any = await checkSlug(slug)
        return response.data.length === 0
      } catch {
        return false
      }
    }

    const isUnique = await isSlugUnique(slugValue)
    if (!isUnique) {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [t("Slug already exists. Please choose another slug.")] }])
    } else {
      form.setFieldsValue({ slug: slugValue })
      form.setFields([{ name: "slug", errors: [] }])
    }
  }

  const handleSubmit = async () => {
    try {
      const values = form.getFieldsValue()
      await createCategory(values)

      onCategoryAdded()
      form.resetFields()
      message.success(t("Category added successfully"))
    } catch {
      message.error(t("Failed to add category"))
    }
  }

  return (
    <>
      {permissions.cateogry.create.enabled && (
        <div className="w-1/2">
          <Title level={4} className="mb-6">
            {t("Add New Category")}
          </Title>
          <Form labelAlign="left" form={form} labelCol={{ span: 5 }} onFinish={handleSubmit} className="max-w-600 mt-5">
            <Flex gap="small" vertical align="flex-start">
              <Form.Item
                className="w-full "
                name="name"
                label={<span className="text-base font-medium">{t("Name")}</span>}
                rules={[{ required: true, message: t("Minimum 3 characters!"), min: 3 }]}
                extra={t("The name is how it appears on your site.")}
              >
                <Input onBlur={handleNameBlur} />
              </Form.Item>

              <div className="flex w-full relative">
                <Form.Item
                  className="w-full"
                  name="slug"
                  label={<span className="text-base font-medium">{t("Slug")}</span>}
                  rules={[{ required: true, message: t("Không được để trống!") }]}
                  extra={t("May not need to be entered (automatically render by name)")}
                >
                  <Input name="slug" className="w-[80%]" disabled={isEditableSlug} />
                </Form.Item>
                <Button
                  className="absolute right-0 py-1 px-2"
                  icon={<EditOutlined />}
                  color={!isEditableSlug ? "primary" : "default"}
                  variant="outlined"
                  onClick={() => setIsEditableSlug(!isEditableSlug)}
                >
                  {t("Edit")}
                </Button>
              </div>

              <Form.Item
                className="w-full description"
                name="description"
                label={<span className="text-base font-medium">{t("Description")}</span>}
                rules={[{ required: true, message: t("Không được để trống!") }]}
                extra={t("The description is not prominent by default; however, some themes may show it.")}
              >
                <Input.TextArea rows={6} className="w-full" />
              </Form.Item>

              <Form.Item className="self-left">
                <Button icon={<PlusCircleOutlined />} size="large" type="primary" htmlType="submit">
                  {t("Add New Category")}
                </Button>
              </Form.Item>
            </Flex>
          </Form>
        </div>
      )}
    </>
  )
}

export default FormAddCategory
